import type {
  AnalyticsApi,
  AnalyticsEvent,
  IdentityApi,
} from '@backstage/core-plugin-api';
import type {
  ActivityEventInput,
  ActivityEventType,
} from '@internal/plugin-activity-common';
import type { ActivityApi } from './ActivityApi';

const SESSION_KEY = 'pocteo.activity.session';

/**
 * Every Backstage plugin already reports what a person is doing — route
 * changes, searches, clicks on results — through the analytics API. Out of
 * the box those events go nowhere, because the default implementation
 * discards them.
 *
 * Implementing that interface is what makes "viewed this component" appear in
 * the trail without a single call site being added to any plugin.
 */
export class ActivityAnalyticsApi implements AnalyticsApi {
  private queue: ActivityEventInput[] = [];
  private timer: ReturnType<typeof setTimeout> | undefined;
  private readonly sessionId: string;
  /** Whether this instance created the session, i.e. the visit is new. */
  private readonly startedSession: boolean;

  private constructor(
    private readonly activityApi: ActivityApi,
    private readonly options: {
      flushIntervalMs: number;
      maxBatch: number;
    },
  ) {
    const session = ActivityAnalyticsApi.sessionId();
    this.sessionId = session.id;
    this.startedSession = session.isNew;

    if (typeof document !== 'undefined') {
      // A person closing the tab is the common case, not the exception. This
      // is the only reliable moment to send what is still queued.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') void this.flush();
      });
    }
  }

  static create(options: {
    activityApi: ActivityApi;
    identityApi?: IdentityApi;
    flushIntervalMs?: number;
    maxBatch?: number;
  }) {
    return new ActivityAnalyticsApi(options.activityApi, {
      flushIntervalMs: options.flushIntervalMs ?? 5_000,
      maxBatch: options.maxBatch ?? 20,
    });
  }

  /**
   * One id per browser tab session, so a day of a person's events can be cut
   * into the visits they actually made.
   */
  private static sessionId(): { id: string; isNew: boolean } {
    if (typeof sessionStorage === 'undefined') {
      return { id: 'no-session', isNew: false };
    }
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return { id: existing, isNew: false };

    const fresh =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `s-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
    sessionStorage.setItem(SESSION_KEY, fresh);
    return { id: fresh, isNew: true };
  }

  /**
   * True when this instance began the visit, which is what distinguishes
   * signing in from reloading a tab that was already signed in.
   */
  get isNewSession(): boolean {
    return this.startedSession;
  }

  get currentSessionId() {
    return this.sessionId;
  }

  private static classify(action: string): ActivityEventType {
    switch (action) {
      case 'navigate':
        return 'view';
      case 'search':
        return 'search';
      case 'click':
      case 'discover':
        return 'click';
      // The app fires these itself on the way in and out. Recognising them
      // here means sign-in is captured even when the session heuristic in
      // SessionRecorder treats the visit as a reload.
      case 'signIn':
      case 'sign-in':
        return 'login';
      case 'signOut':
      case 'sign-out':
        return 'logout';
      default:
        return 'unknown';
    }
  }

  /**
   * The catalog puts the entity ref in the analytics context on entity pages,
   * but not every route does. Falling back to the path means an entity view
   * is still attributed correctly if that context is ever dropped.
   */
  private static entityRefFrom(event: AnalyticsEvent): string | undefined {
    const fromContext = (event.context as Record<string, unknown> | undefined)
      ?.entityRef;
    if (typeof fromContext === 'string' && fromContext.includes('/')) {
      return fromContext;
    }

    const match = /^\/catalog\/([^/]+)\/([^/]+)\/([^/?#]+)/.exec(
      event.subject ?? '',
    );
    if (match) {
      const [, namespace, kind, name] = match;
      return `${kind.toLowerCase()}:${namespace}/${name}`;
    }
    return undefined;
  }

  captureEvent(event: AnalyticsEvent): void {
    const context = (event.context ?? {}) as Record<string, unknown>;

    this.queue.push({
      timestamp: new Date().toISOString(),
      type: ActivityAnalyticsApi.classify(event.action),
      action: event.action,
      subject: event.subject,
      entityRef: ActivityAnalyticsApi.entityRefFrom(event),
      pluginId:
        typeof context.pluginId === 'string' ? context.pluginId : undefined,
      sessionId: this.sessionId,
      meta: {
        ...(event.value !== undefined ? { value: event.value } : {}),
        ...(typeof context.routeRef === 'string'
          ? { routeRef: context.routeRef }
          : {}),
        ...(typeof context.extension === 'string'
          ? { extension: context.extension }
          : {}),
        ...(event.attributes ?? {}),
      },
    });

    if (this.queue.length >= this.options.maxBatch) {
      void this.flush();
      return;
    }
    if (!this.timer) {
      this.timer = setTimeout(() => {
        this.timer = undefined;
        void this.flush();
      }, this.options.flushIntervalMs);
    }
  }

  /** Records something the analytics API cannot see, such as signing out. */
  record(event: ActivityEventInput): void {
    this.queue.push({ sessionId: this.sessionId, ...event });
    void this.flush();
  }

  async flush(): Promise<void> {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
    if (this.queue.length === 0) return;
    const batch = this.queue;
    this.queue = [];
    await this.activityApi.record(batch);
  }
}
